import { useEffect, useMemo, useState } from 'react'
import type { Translate } from '@webbuilder/i18n'
import { useI18n, translate } from '../../../lib/i18n'
import { SectionBar } from '../SectionBar'
import { AudienceMap } from './AudienceMap'
import { analyticsQueryKey, getProjectAnalytics, type AnalyticsQuery, type BillingPlan, type ProjectAnalytics } from '../../../lib/api'
import { CompareSelect, DateRangePicker, rangeLabel } from '../DateRangePicker'
import { TrafficChart } from '../TrafficChart'
import { getCachedAnalytics, setCachedAnalytics } from '../../../lib/analyticsCache'
import { fakeAnalytics } from '../../../lib/fakeAnalytics'
import { FeatureUpsell } from '../FeatureUpsell'
import { panel, RowInfo, SplitDonut, StatCard, TopList, trend } from './analyticsShared'
import { IconCalendarEvent, IconChartBar, IconClick, IconDevices, IconFileText, IconSpeakerphone, IconUsers, IconWorld } from '@tabler/icons-react'

/**
 * Código ISO → nombre del país en el idioma de quien mira: «MX» es «México» o
 * «Mexico» según el panel.
 *
 * Estaba fijo en español, así que un panel en inglés listaba «España» y
 * «Estados Unidos» entre datos ingleses. `Intl.DisplayNames` acepta cualquier
 * cadena sin quejarse, que es lo que hace este fallo tan fácil de dejar pasar.
 *
 * Se construye por llamada y no una vez a nivel de módulo porque el idioma
 * puede cambiar sin recargar; `Intl` cachea internamente, así que no cuesta.
 */
function countryLabel(code: string, language: string, t: Translate): string {
  // 'XX' is what the worker stores when Cloudflare gives no country (local dev).
  if (!code || code === 'XX') return t('analytics.unknownCountry')
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(code) ?? code
  } catch {
    return code
  }
}

/**
 * Turns a raw request path into a human name for the analytics UI.
 * "/" is the home page, so it reads "Inicio"; deeper paths keep their slug but
 * lose the leading slash and turn hyphens into spaces ("/sobre-nosotros" →
 * "Sobre nosotros"). The raw path stays available as a secondary line.
 */
function pathLabel(path: string): string {
  if (!path || path === '/') return translate('pages.home')
  const last = path.replace(/\/+$/, '').split('/').pop() ?? path
  const words = last.replace(/-/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : path
}

/**
 * Orden fijo de los dispositivos y su color, atado al aparato y nunca a su
 * puesto en la lista: reordenar o que uno se quede a cero no puede repintar a
 * los que quedan.
 */
/* Son DATOS, no texto: el servidor devuelve el dispositivo con estos nombres y
   aquí se usan para casar y para colorear. Traducirlos rompería la coincidencia
   y dejaría todos los dispositivos del mismo color. Lo que se enseña pasa por
   `deviceLabel`. */
const DEVICE_ORDER = ['Móvil', 'Escritorio', 'Tablet']
// Monochromatic blue ramp — the mascot's own gradient — shared with the traffic
// chart and the audience map. Strongest device first; lighter steps follow.
/* La rampa completa, para lo que no está en el catálogo de abajo. */
const DEVICE_RAMP: readonly [string, string, string] = [
  'var(--dash-analytics-ink)',
  'var(--dash-analytics-ink-2)',
  'var(--dash-analytics-ink-3)',
] as const
const DEVICE_COLOR: Record<string, string> = {
  'Móvil': 'var(--dash-analytics-ink)',
  'Escritorio': 'var(--dash-analytics-ink-2)',
  'Tablet': 'var(--dash-analytics-ink-3)',
}

/**
 * El dispositivo crudo del worker → su nombre en el idioma del panel.
 *
 * Se enseñaba tal cual, así que un panel en inglés decía «Escritorio». Lo que
 * no está en el catálogo (una cadena vieja o futura) sale sin tocar.
 */
const DEVICE_KEY: Record<string, 'device.mobile' | 'device.desktop' | 'device.tablet'> = {
  'Móvil': 'device.mobile',
  'Escritorio': 'device.desktop',
  'Tablet': 'device.tablet',
}

function deviceLabel(device: string, t: Translate): string {
  const key = DEVICE_KEY[device]
  return key ? t(key) : device
}

/** 'Directo' es el único valor fijo que guarda el worker como fuente; el resto son dominios. */
function sourceLabel(source: string, t: Translate): string {
  return source === 'Directo' ? t('an.direct') : source
}

export function AnalyticsSection({ projectId, token, publishedSubdomain, plan, onOpenPlans, onOpenPage }: {
  projectId: string
  token: string
  publishedSubdomain: string | null
  plan: BillingPlan
  onOpenPlans: () => void
  /** Abrir las estadísticas de la página de esa ruta. Ver `TopListRow.onOpen`. */
  onOpenPage?: ((path: string) => void) | undefined
}) {
  const i18n = useI18n()
  const { t, language } = i18n
  // Paid feature. Free users never fetch real data — they get a fake dataset
  // rendered blurred behind the Pro upsell (so DevTools shows no real numbers).
  const isPaid = plan !== 'free'
  /**
   * Por defecto: últimos 30 días contra los 30 anteriores.
   *
   * Es la pregunta que se hace uno al abrir el panel — "¿voy mejor que el mes
   * pasado?" — y no exige elegir nada para responderla.
   */
  const [query, setQuery] = useState<AnalyticsQuery>({ range: '30d', compare: 'previous' })
  const queryKey = analyticsQueryKey(query)
  const [realStats, setRealStats] = useState<ProjectAnalytics | null>(() => (isPaid ? getCachedAnalytics(projectId, queryKey) ?? null : null))
  const [realLoading, setRealLoading] = useState(() => isPaid && !getCachedAnalytics(projectId, queryKey))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isPaid) { setRealLoading(false); return }
    let cancelled = false
    const cached = getCachedAnalytics(projectId, queryKey)
    // Show cached data instantly; only skeleton on a true first load of this range.
    if (cached) { setRealStats(cached); setRealLoading(false) } else { setRealLoading(true) }
    setError(null)
    void (async () => {
      try {
        const data = await getProjectAnalytics(token, projectId, query)
        if (cancelled) return
        setCachedAnalytics(projectId, queryKey, data)
        setRealStats(data)
      } catch (err) {
        if (cancelled) return
        if (!cached) setRealStats(null)
        setError(err instanceof Error ? err.message : t('an.loadFailed'))
      }
      if (!cancelled) setRealLoading(false)
    })()
    return () => { cancelled = true }
    // `queryKey` resume la consulta entera; `query` es un objeto nuevo en cada
    // render y dispararía el efecto sin parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token, queryKey, isPaid])

  const fake = useMemo(() => fakeAnalytics(query.range === 'custom' ? '30d' : query.range), [query.range])
  const stats = isPaid ? realStats : fake
  const loading = isPaid ? realLoading : false

  const topPage = stats?.topPages[0]

  /** Orden fijo primero, y detrás lo que no se reconozca. Nunca por volumen. */
  const deviceSlices = [
    ...DEVICE_ORDER.map((name) => (stats?.devices ?? []).find((row) => row.device === name)).filter((row) => !!row),
    ...(stats?.devices ?? []).filter((row) => !DEVICE_ORDER.includes(row.device)),
  ].map((row, index) => ({
    key: row.device,
    label: deviceLabel(row.device, t),
    value: row.views,
    /*
     * Lo desconocido baja por la rampa, no se queda en un color fijo.
     *
     * El respaldo era siempre `ink-3`, así que en cuanto llegaban dos valores
     * fuera del catálogo —una cadena vieja, una que el detector todavía no
     * reconoce— salían del mismo color y el pastel dejaba de tener sectores
     * distinguibles. Repartirlos por posición no acierta el color «correcto»,
     * pero sí garantiza que se vean como tramos distintos, que es de lo que
     * vive un gráfico de sectores.
     */
    color: DEVICE_COLOR[row.device] ?? DEVICE_RAMP[index % DEVICE_RAMP.length] ?? DEVICE_RAMP[0],
  }))

  /**
   * Cómo se nombra el tramo contra el que se mide.
   *
   * Con fechas concretas en vez de "periodo anterior": un porcentaje sin saber
   * contra qué se calculó no se puede juzgar, y menos aún cuando el tramo es
   * personalizado.
   */
  const comparison = stats?.comparison
    ? query.compare === 'year'
      ? t('range.compare.year').toLocaleLowerCase(language)
      : rangeLabel(i18n, { range: 'custom', from: stats.comparison.from, to: stats.comparison.to })
    : null

  const content = (
    <div className="dash-analytics-view" style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 64 }} aria-busy={loading}>
      <SectionBar title={t('analytics.title')} subtitle={t('analytics.subtitle')}>
        <div className="dash-toolbar dash-layered-toolbar dash-analytics-range-toolbar">
          {isPaid ? (
            <>
              <DateRangePicker
                query={query}
                window={stats?.window}
                timezone={stats?.timezone}
                onChange={setQuery}
              />
              <CompareSelect query={query} onChange={setQuery} />
            </>
          ) : (
            <span className="dash-secondary-btn dash-range-trigger is-locked">
              <IconCalendarEvent size={14} aria-hidden="true" />
              <span>{t('analytics.last30')}</span>
            </span>
          )}
        </div>
      </SectionBar>

      {error ? (
        <div className="dash-analytics-content-in dash-card-description" style={{ ...panel, padding: 40, textAlign: 'center' }}>
          {error}
        </div>
      ) : !loading && (!stats || stats.totals.views === 0) ? (
        /* El mismo hueco vacío que el resto del panel —trazo fino, titular y
           frase—, en la tarjeta que `.dash-placeholder` ya trae puesta. Sin
           botón: una visita no se crea desde aquí, la trae quien entra al
           sitio. */
        <div className="dash-analytics-content-in dash-placeholder">
          <IconChartBar size={34} aria-hidden="true" />
          <strong>{t('analytics.emptyTitle')}</strong>
          <span>{t('analytics.emptyBody')}</span>
        </div>
      ) : (
        <>
          <div className="dash-loaded-content-in dash-auto-grid">
            <StatCard
              label={t('analytics.pageViews')}
              value={stats?.totals.views.toLocaleString() ?? ''}
              icon={IconClick}
              change={stats ? trend(stats.totals.views, stats.totals.viewsPrev) : null}
              loading={loading}
            />
            <StatCard
              label={t('analytics.uniqueVisitors')}
              value={stats?.totals.uniques.toLocaleString() ?? ''}
              icon={IconUsers}
              change={stats ? trend(stats.totals.uniques, stats.totals.uniquesPrev) : null}
              loading={loading}
            />
            <StatCard label={t('analytics.countries')} value={stats?.countriesCount.toLocaleString() ?? ''} icon={IconWorld} loading={loading} />
            <StatCard label={t('analytics.topPage')} value={topPage ? pathLabel(topPage.path) : '—'} icon={IconFileText} loading={loading} />
          </div>

          <TrafficChart
            series={stats?.series ?? []}
            comparison={stats?.comparison?.series ?? []}
            comparisonLabel={comparison}
            granularity={stats?.granularity}
            timezone={stats?.timezone}
            loading={loading}
          />

          <div className="dash-analytics-geo-row dash-loaded-content-in">
            <div className="dash-analytics-map-panel" style={{ ...panel, padding: 20 }}>
              <div className="dash-analytics-map-head dash-analytics-card-heading">
                <span className="dash-widget-glass-icon dash-stat-glass-icon" aria-hidden="true"><IconWorld size={14} /></span>
                <div>
                  <h3 className="dash-card-title">{t('analytics.viewsByCountry')}</h3>
                </div>
              </div>
              {/* El mapa espera `(code) => string`: se le pasa ya atado al idioma
                  vigente en vez de cambiarle la firma, que la comparte con
                  otras pantallas. */}
              <AudienceMap countries={stats?.topCountries ?? []} labelFor={(code) => countryLabel(code, language, t)} />
            </div>
            <TopList
              title={t('analytics.countries')}
              icon={IconWorld}
              rows={(stats?.topCountries ?? []).map((country) => ({ key: country.country, label: countryLabel(country.country, language, t), value: country.views.toLocaleString() }))}
              empty={t('an.noCountries')}
              loading={loading}
              fillHeight
              scrollHint={t('an.moreCountries')}
            />
          </div>

          <div className="dash-loaded-content-in dash-auto-grid is-wide">
            <TopList
              title={t('analytics.topPages')}
              icon={IconFileText}
              rows={(stats?.topPages ?? []).map((page) => ({
                key: page.path,
                label: pathLabel(page.path),
                sub: page.path,
                value: page.views.toLocaleString(),
                deleted: page.exists === false,
                ...(onOpenPage ? { onOpen: () => onOpenPage(page.path) } : {}),
              }))}
              empty={t('an.noPages')}
              loading={loading}
            />
            <TopList
              title={t('analytics.trafficSources')}
              icon={IconClick}
              rows={(stats?.topSources ?? []).map((source) => ({ key: source.source, label: sourceLabel(source.source, t), value: source.views.toLocaleString() }))}
              empty={t('an.noSources')}
              loading={loading}
            />
            <SplitDonut
              title={t('analytics.devices')}
              icon={IconDevices}
              slices={deviceSlices}
              empty={t('an.noDevices')}
              unit={t('an.viewsUnit')}
              loading={loading}
            />
            {/* Hidden for untagged sites: a permanently empty panel is noise for
                the many owners who never run a tagged campaign. */}
            {(stats?.topCampaigns?.length ?? 0) > 0 ? (
              <TopList
                title={t('analytics.campaigns')}
                icon={IconSpeakerphone}
                rows={(stats?.topCampaigns ?? []).map((campaign) => {
                  const fullUrl = publishedSubdomain
                    ? `https://${publishedSubdomain}.veltica.com/?utm_campaign=${campaign.campaign}`
                    : `?utm_campaign=${campaign.campaign}`
                  return {
                    key: campaign.campaign,
                    label: campaign.campaign,
                    // Short form in the row; the full link lives in the hover card.
                    sub: `?utm_campaign=${campaign.campaign}`,
                    value: campaign.views.toLocaleString(),
                    info: (
                      <RowInfo
                        title={campaign.campaign}
                        sub={fullUrl}
                        stats={[
                          { term: t('an.views'), detail: campaign.views.toLocaleString() },
                        ]}
                        sections={[
                          { label: t('an.source'), rows: (campaign.sources ?? []).map((row) => ({ ...row, value: sourceLabel(row.value, t) })) },
                          { label: t('an.medium'), rows: campaign.mediums ?? [] },
                          { label: t('an.content'), rows: campaign.contents ?? [] },
                          { label: t('an.term'), rows: campaign.terms ?? [] },
                          { label: t('an.device'), rows: (campaign.devices ?? []).map((row) => ({ ...row, value: deviceLabel(row.value, t) })) },
                        ]}
                      />
                    ),
                  }
                })}
                empty={t('an.noCampaigns')}
                loading={loading}
              />
            ) : null}
          </div>
        </>
      )}
    </div>
  )

  return isPaid ? content : <FeatureUpsell onOpenPlans={onOpenPlans} variant="full">{content}</FeatureUpsell>
}
